import { Container, Graphics, Text } from "pixi.js"
import { SCALER } from "../../../../client_util"
import { TextRenderer } from "../text/text_renderer"
import { disposeTextTextures } from "../text/dispose_text"
import { WaveRevealFrame, waveRevealHeadline } from "./run_summary_format"

// The opening beat of the end-of-run scorecard: how far the camp got, in the biggest type the
// screen has, before the panel itself is allowed to say anything. See run_summary_format for the
// timing (it is one pure function of the clock) and run_summary.ts for who drives it.
//
// The number is the whole point, so everything here is arranged to make it land: it arrives
// oversized and snaps down past its resting size, a shockwave goes out from it, a bloom of warm
// light sits behind it, embers climb past it while it is held, and the two smaller lines fade in
// after it rather than with it. Then it lifts away and the panel comes up through the space it
// leaves.
//
// Drawn in the same language as the panel below it (warm amber, dark glass, no blur filters --
// fake glows out of layered fills, which is what the rest of the ember UI does for the sake of
// weak GPUs).

// Geometry, as the centre line of each element relative to the block's own centre. The stage
// stretches everything vertically by SCALER, so anything that should LOOK round (the shockwave,
// the bloom, the diamond) is drawn squashed.
const HEADLINE_SIZE = 58
const KICKER_CY = -58
const HEADLINE_CY = 0
const RULE_Y = 48
const EPITHET_CY = 62

const GOLD = 0xffd23f // the run's number, in the same gold the panel gives "WAVES HELD"
const GOLD_HOT = 0xffb04a
const RIM = 0xf2a94e
const EMBER_CORE = 0xffd98a
const SHADOW = 0x140a04

// The bloom behind the number: concentric filled ellipses, low alpha, biggest first. Banded fills
// rather than a blur filter, for the same reason the panel's ash shadow is banded.
const BLOOM_BANDS = 7
const BLOOM_RX = 260
const BLOOM_RY = 104 / SCALER

// The shockwave: two strokes going out from the strike, fading as they widen.
const RING_RX_FROM = 70
const RING_RX_TO = 330
const RING_ASPECT = 0.38

// Embers climbing past the held number. Emitted on a clock while the frame says to, capped so a
// long hold can't grow the list without bound.
const MOTE_EVERY_MS = 46
const MOTE_MAX = 44
const MOTE_SPREAD_X = 210

interface Mote {
    x: number
    y: number
    vx: number
    vy: number
    age: number
    life: number
    r: number
}

export class WaveReveal extends Container {
    // Everything that moves as one under the slam: the bloom and the three lines of type.
    private block: Container
    private ring: Graphics
    private motes: Graphics

    // The two small lines, kept so their staggered fades can be applied without walking children.
    private kicker?: Text
    private epithet?: Text

    private moteList: Mote[] = []
    private moteClock = 0

    constructor() {
        super()

        this.ring = new Graphics()
        this.addChild(this.ring)

        this.block = new Container()
        this.addChild(this.block)

        // Above the type, so an ember can pass in front of the number as well as behind the bloom.
        this.motes = new Graphics()
        this.addChild(this.motes)

        this.visible = false
    }

    // Rebuild around a run. Safe to call again -- the previous build is torn down first.
    public setSummary(summary: { wavesReached?: number }) {
        this.teardown()

        const { kicker, headline, epithet } = waveRevealHeadline(summary)
        const block = this.block

        const bloom = new Graphics()
        for (let i = BLOOM_BANDS; i >= 1; i--) {
            const t = i / BLOOM_BANDS
            // The outer bands are nearly nothing; the light gathers towards the letters.
            bloom.beginFill(GOLD_HOT, 0.055 * (1 - t) + 0.012)
            bloom.drawEllipse(0, HEADLINE_CY, BLOOM_RX * t, BLOOM_RY * t)
            bloom.endFill()
        }
        block.addChild(bloom)

        this.kicker = this.addLine(block, kicker, { size: 13, color: RIM, cy: KICKER_CY })

        // The number, in three passes: a dark copy for weight, a hotter oversized copy for the
        // glow around the letters, and the letters themselves on top.
        this.addHeadline(block, headline, SHADOW, 1, 3, 0.55)
        this.addHeadline(block, headline, GOLD_HOT, 1.06, 0, 0.38)
        this.addHeadline(block, headline, GOLD, 1, 0, 1)

        // A hairline rule with a diamond in it, separating the number from its verdict.
        const rule = new Graphics()
        const gap = 12
        const reach = 92
        rule.lineStyle(1, RIM, 0.45)
        rule.moveTo(-gap - reach, RULE_Y)
        rule.lineTo(-gap, RULE_Y)
        rule.moveTo(gap, RULE_Y)
        rule.lineTo(gap + reach, RULE_Y)
        rule.lineStyle(0)
        rule.beginFill(RIM, 0.7)
        rule.drawPolygon([0, RULE_Y - 4 / SCALER, 4, RULE_Y, 0, RULE_Y + 4 / SCALER, -4, RULE_Y])
        rule.endFill()
        block.addChild(rule)

        this.epithet = this.addLine(block, epithet, { size: 11, color: RIM, cy: EPITHET_CY })

        this.moteList = []
        this.moteClock = 0
        this.ring.clear()
        this.motes.clear()
        this.block.alpha = 0
        this.visible = true
    }

    // TextRenderer has no letter-spacing, so these lines get theirs the only way a plain Text can:
    // spaces between the characters. Worth it -- they read as an inscription rather than a
    // caption, which is the difference between this beat and a tooltip.
    private spaceOut(text: string): string {
        return text.split("").join(" ")
    }

    // Anchored rather than offset by half its width: the whole block is scaled through the slam,
    // and an anchored Text stays put under a scale while an offset one drifts.
    private addLine(parent: Container, text: string, opts: { size: number; color: number; cy: number }): Text {
        const t = new TextRenderer()
            .fontSize(opts.size)
            .color(opts.color)
            .bold()
            .align("center")
            .render(this.spaceOut(text)) as Text
        t.anchor.set(0.5, 0.5)
        t.y = opts.cy
        t.alpha = 0
        parent.addChild(t)
        return t
    }

    // One pass of the number. `scale` is baked into this Text rather than taken from the block, so
    // the glow copy stays a fixed amount bigger than the letters through the whole slam.
    private addHeadline(parent: Container, text: string, color: number, scale: number, dy: number, alpha: number) {
        const t = new TextRenderer().fontSize(HEADLINE_SIZE).color(color).bold().align("center").render(text) as Text
        t.anchor.set(0.5, 0.5)
        t.scale.set(scale)
        t.y = HEADLINE_CY + dy
        t.alpha = alpha
        parent.addChild(t)
    }

    // ===== per-frame ===================================================================

    // Draw one already-computed moment of the beat (see waveRevealFrame). `dtMs` is only needed by
    // the part that carries its own state: the embers.
    public applyFrame(frame: WaveRevealFrame, dtMs: number = 0) {
        if (frame.done) {
            this.visible = false
            return
        }

        this.visible = true
        this.block.alpha = frame.alpha
        this.block.scale.set(frame.scale)
        this.block.y = -frame.lift

        // The small lines fade in UNDER the block's own alpha, so neither can outrun the number.
        if (this.kicker) {
            this.kicker.alpha = frame.kickerAlpha
        }
        if (this.epithet) {
            this.epithet.alpha = frame.epithetAlpha
        }

        this.drawRing(frame.ringT)
        this.advanceMotes(dtMs, frame.emitting, frame.alpha)
    }

    private drawRing(t: number) {
        const g = this.ring
        g.clear()
        if (t < 0) {
            return
        }
        const eased = 1 - Math.pow(1 - t, 3)
        const left = Math.pow(1 - t, 2)
        for (let i = 0; i < 2; i++) {
            const rx = RING_RX_FROM + (RING_RX_TO - RING_RX_FROM) * eased - i * 26
            if (rx <= 0) {
                continue
            }
            g.lineStyle(3 * left + 0.6, i === 0 ? GOLD : GOLD_HOT, (0.55 - i * 0.2) * left)
            g.drawEllipse(0, HEADLINE_CY, rx, (rx * RING_ASPECT) / SCALER)
        }
    }

    // Embers thrown up past the held number. One Graphics, cleared and refilled each frame -- a
    // few dozen filled circles, which costs nothing next to the paperdoll the panel is building.
    private advanceMotes(dtMs: number, emitting: boolean, alpha: number) {
        if (emitting) {
            this.moteClock += dtMs
            while (this.moteClock >= MOTE_EVERY_MS && this.moteList.length < MOTE_MAX) {
                this.moteClock -= MOTE_EVERY_MS
                this.moteList.push({
                    x: (Math.random() - 0.5) * MOTE_SPREAD_X * 2,
                    y: 46 + Math.random() * 26,
                    vx: (Math.random() - 0.5) * 0.02,
                    vy: -(0.045 + Math.random() * 0.055),
                    age: 0,
                    life: 700 + Math.random() * 700,
                    r: 0.9 + Math.random() * 1.5,
                })
            }
        } else {
            this.moteClock = 0
        }

        this.moteList = this.moteList.filter(mote => {
            mote.age += dtMs
            mote.x += mote.vx * dtMs
            mote.y += mote.vy * dtMs
            return mote.age < mote.life
        })

        const g = this.motes
        g.clear()
        g.lineStyle(0)
        this.moteList.forEach(mote => {
            const left = 1 - mote.age / mote.life
            // In fast, out slow: an ember is brightest just after it leaves the fire.
            const heat = Math.min(1, left * 3) * left * alpha
            if (heat <= 0.02) {
                return
            }
            g.beginFill(GOLD_HOT, 0.16 * heat)
            g.drawCircle(mote.x, mote.y, mote.r * 2.4)
            g.endFill()
            g.beginFill(EMBER_CORE, 0.9 * heat)
            g.drawCircle(mote.x, mote.y, mote.r)
            g.endFill()
        })
    }

    // ===== teardown ====================================================================

    // Free the number's textures (a 58pt Text is not a small canvas to hold onto until the next
    // death) and reset every piece of running state.
    public teardown() {
        disposeTextTextures(this.block)
        this.block.removeChildren().forEach(child => {
            if (!child.destroyed) {
                child.destroy()
            }
        })
        this.kicker = undefined
        this.epithet = undefined
        this.ring.clear()
        this.motes.clear()
        this.moteList = []
        this.moteClock = 0
        this.block.alpha = 0
        this.block.scale.set(1)
        this.block.y = 0
        this.visible = false
    }
}
